import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { notificationService } from '@/lib/services/notification.service';
import { messageText } from '../lib/message-keys';
import { readProblem } from '../lib/problem';
import { useUpdateFormat } from '../model/useFormatLibrary';
import type { FormatPatch, SaveResult } from '../model/types';

/**
 * Saves a format edit and explains every refusal the server can give: a breaking change waits
 * until `confirmation` is answered (render it with `BreakingChangeDialog`), a stale revision and an
 * invalid value are reported in words, and nothing is retried behind the editor's back.
 */
export function useFormatSaver(): {
  save: (key: string, patch: FormatPatch) => Promise<boolean>;
  isSaving: boolean;
  confirmation: { open: boolean; onCancel: () => void; onConfirm: () => void };
} {
  const { t } = useTranslation('admin');
  const update = useUpdateFormat();
  type Pending = { key: string; patch: FormatPatch; resolve: (ok: boolean) => void };
  const [pending, setPending] = useState<Pending | null>(null);
  // Mirrors `pending` so the resolver is reachable from the unmount cleanup, which cannot read state.
  const pendingRef = useRef<Pending | null>(null);

  useEffect(() => () => pendingRef.current?.resolve(false), []);

  const report = (result: SaveResult) => {
    notificationService.showSuccess(
      result.changedRecords === 0
        ? t('admin.adFormats.nothingChanged')
        : t('admin.adFormats.saved', { version: result.datasetVersion }),
    );
  };

  const attempt = async (key: string, patch: FormatPatch): Promise<boolean> => {
    try {
      report(await update.mutateAsync({ key, patch }));
      return true;
    } catch (error) {
      const problem = readProblem(error);
      if (problem.code === 'breaking_change_unconfirmed') {
        return await new Promise<boolean>((resolve) => {
          const next = { key, patch, resolve };
          pendingRef.current = next;
          setPending(next);
        });
      }
      if (problem.code === 'version_conflict') {
        notificationService.showError(t('admin.adFormats.errors.versionConflict'));
      } else if (problem.code === 'validation_failed') {
        notificationService.showError(
          [
            t('admin.adFormats.errors.validationFailed'),
            ...problem.issues.map((issue) => messageText(t, issue)),
          ].join(' '),
        );
      } else if (problem.status === 409 || problem.status === 422) {
        notificationService.showError(t('admin.adFormats.errors.generic'));
      }
      return false;
    }
  };

  const close = (confirmed: boolean) => {
    const current = pendingRef.current;
    if (!current) return;
    const { key, patch, resolve } = current;
    pendingRef.current = null;
    setPending(null);
    if (confirmed) void attempt(key, { ...patch, confirmBreaking: true }).then(resolve);
    else resolve(false);
  };

  return {
    save: attempt,
    isSaving: update.isPending,
    confirmation: {
      open: pending !== null,
      onCancel: () => close(false),
      onConfirm: () => close(true),
    },
  };
}
